/**
 * ground_geometry — put the ground rules' per-vertex buffer (lib/ground_layers.ts
 * layout) on a terrain geometry, and pack it into ONE interleaved vertex buffer.
 *
 * Order matters on a chunk: attach (five plain vec4 attributes) → cave cut
 * (index only) → skirt (lib/terrain_skirt.ts grows every PLAIN attribute,
 * copying the border vertex's data onto the apron) → interleave. Interleaving
 * last keeps the skirt code attribute-agnostic; three keys WebGPU vertex
 * buffers by the underlying buffer, so the five attributes then cost ONE of
 * the eight (verify:ground-rules counts them per geometry kind).
 */
import * as THREE from 'three/webgpu';
import { GROUND_ATTRS, GROUND_STRIDE } from './ground_layers.js';

/** Attach a GROUND_STRIDE-per-vertex buffer as five vec4 attributes (copies — the buffer stays the caller's). */
export function attachGround(geo: THREE.BufferGeometry, buf: Float32Array): void {
  const count = buf.length / GROUND_STRIDE;
  for (const a of GROUND_ATTRS) {
    const arr = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      const s = i * GROUND_STRIDE + a.offset, d = i * 4;
      arr[d] = buf[s]; arr[d + 1] = buf[s + 1]; arr[d + 2] = buf[s + 2]; arr[d + 3] = buf[s + 3];
    }
    geo.setAttribute(a.name, new THREE.BufferAttribute(arr, 4));
  }
}

/** Replace the five plain ground attributes by views into ONE interleaved buffer (after the skirt). */
export function interleaveGround(geo: THREE.BufferGeometry): boolean {
  const first = geo.getAttribute(GROUND_ATTRS[0].name) as THREE.BufferAttribute | undefined;
  if (!first || (first as unknown as { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute) return false;
  const count = first.count;
  const data = new Float32Array(count * GROUND_STRIDE);
  for (const a of GROUND_ATTRS) {
    const src = (geo.getAttribute(a.name) as THREE.BufferAttribute).array as Float32Array;
    for (let i = 0; i < count; i++) {
      const s = i * 4, d = i * GROUND_STRIDE + a.offset;
      data[d] = src[s]; data[d + 1] = src[s + 1]; data[d + 2] = src[s + 2]; data[d + 3] = src[s + 3];
    }
  }
  const ib = new THREE.InterleavedBuffer(data, GROUND_STRIDE);
  for (const a of GROUND_ATTRS) geo.setAttribute(a.name, new THREE.InterleavedBufferAttribute(ib, 4, a.offset));
  return true;
}

/** Distinct vertex buffers a geometry binds for the attributes a shader reads (the WebGPU limit is 8). */
export function vertexBufferCount(geo: THREE.BufferGeometry, read: readonly string[]): number {
  const bufs = new Set<unknown>();
  for (const name of read) {
    const at = geo.getAttribute(name) as (THREE.BufferAttribute & { data?: unknown }) | undefined;
    if (!at) continue;
    bufs.add((at as unknown as { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute ? at.data : at);
  }
  return bufs.size;
}

/** The attributes the v2 ground shader reads (the census reads them off every geometry kind). */
export const GROUND_V2_READS = ['position', 'normal', ...GROUND_ATTRS.map((a) => a.name)] as const;
/** The attributes the v1 ground shader reads. */
export const GROUND_V1_READS = ['position', 'normal', 'uv', 'biomeWA', 'biomeWB', 'roadW', 'fieldW'] as const;
